import { useStateManager } from '@/composables/useStateManager'
import { BooleanObject } from '@/models/GenericInterfaces'
import { Culture } from '@/models/Global'
import { ImportResult, Modified, ProductInput, ProductInputRecord } from '@/models/Import'
import { SimplifiedProduct } from '@/models/Product'
import { AdvancedProperty, EditableAdvancedProperty, SelectedPropertyRecord } from '@/models/Property'
import DataService from '@/services/DataService'
import PropertyService from '@/services/PropertyService'
import { browserStorage } from '@/utils/browserStorage'
import { getProductInputEntries } from '@/utils/importUtils'
import { getPropertyKey, isSkuProperty } from '@/utils/propertyUtils'
import { clamp, cultureIsIncluded, arrayToRecord, objectsAreEqual, toComparable } from '@/utils/tools'
import { defineStore } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDefaultStore } from '.'
import { useEditingSessionsStore } from './editingSessionsStore'
import { useProductsStore } from './productsArchiveStore'
import { usePropertyStore } from './propertyStore'
import { useTaxonomiesStore } from './taxonomiesStore'
import { useSelectedProperties } from '@/composables/useSelectedProperties'

export const useProductsBulkStore = defineStore('products-bulk-store', () => {
  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()
  const productsStore = useProductsStore()
  const sessionStore = useEditingSessionsStore()
  const propertyStore = usePropertyStore()
  const taxonomiesStore = useTaxonomiesStore()
  const route = useRoute()
  const { initProperties, properties, selectedProperties, selectedPropertyKeys, selectedDeadlines, selectedCultures, propertyKeysIncludedInSelectedDeadlines } =
    useSelectedProperties('products')

  const maximumSkeletonRows = 25

  // -----------------------
  // -- Reactives (state) --
  // -----------------------
  const stateManager = useStateManager<ProductInputRecord>()

  const editMode = ref(false)

  const extraProducts = ref<SimplifiedProduct[]>([]) // existing products not matching filter but exists in the product inputs
  const newProducts = ref<SimplifiedProduct[]>([]) // new rows

  const modified = ref<Modified>({})
  const activateProductLinks = ref(!!browserStorage.get('productTable-activateFilterLinks'))
  const latestImportedProducts = ref<ImportResult>()

  const showRecentImportedProductsMsg = ref(true)

  // ------------------------------
  // -- Computed props (getters) --
  // ------------------------------

  const newProductsSkus = computed(() => new Set(newProducts.value.map((p) => p.sku)))
  const newProductsBySku = computed(() => arrayToRecord(newProducts.value, 'sku'))
  // products from prop and potentially products that was edited in a previous session, but no longer matching current filters due to changes
  const sessionProducts = computed(() => [...(productsStore.currentProducts || []), ...extraProducts.value, ...newProducts.value])
  const sessionProductsBySku = computed(() =>
    sessionProducts.value?.reduce((acc: Partial<{ [sku: string]: SimplifiedProduct }>, product: SimplifiedProduct | undefined) => {
      if (product) {
        acc[product.sku] = product
      }
      return acc
    }, {}),
  )
  const allProducts = computed(() => sessionProducts.value.concat(productsStore.expiredProducts || []))

  const nonDefaultCulturalProperties = computed(() =>
    properties.value?.filter(
      (property) => (property.type !== 'languageSpecific' && property.type !== 'countrySpecific') || !!property.culturePrefix, // filter out "default" cultural properties (properties without culturePrefix) for products bulk
    ),
  )
  const nonDefaultCulturalPropertiesByComparable = computed(() => arrayToRecord(nonDefaultCulturalProperties.value || [], 'defaultHeader', toComparable))

  const previewProperties = computed(() => nonDefaultCulturalProperties.value?.filter((property) => !property.readonly))

  const stickyProperties = computed(() => properties.value?.filter((property) => property.sticky) || [])

  // stickies and selected
  const combinedProperties = computed(() => stickyProperties.value.concat(selectedProperties.value || []))
  // used by columnn search
  const combinedPropertiesByComparable = computed(() =>
    combinedProperties.value.reduce((acc: Record<string, { index: number; property: EditableAdvancedProperty } | undefined>, property, index) => {
      acc[toComparable(property.defaultHeader)] = { index, property }
      return acc
    }, {}),
  )

  const allCulturesSelected = computed(() => selectedCultures.value?.length === store.availableCultures.length)

  const recentImportedProductsNotAmongResult = computed(() => latestImportedProducts.value?.imported?.filter((sku) => !sessionProductsBySku.value[sku]))

  const productInputEntries = computed(() => getProductInputEntries(stateManager.currentState.value))
  /** sanitized state */
  const productInputBySku = computed(() => Object.fromEntries(productInputEntries.value))
  const isPristine = computed(() => Object.keys(productInputBySku.value).length === 0)

  const totalRows = computed(() => productsStore.currentTotalProducts + extraProducts.value.length + newProducts.value.length)
  const skeletonRows = computed(() => clamp(totalRows.value - (sessionProducts.value?.length || 0), 0, maximumSkeletonRows))

  // --------------
  // -- Watchers --
  // --------------
  watch(activateProductLinks, (value) => {
    browserStorage.set('productTable-activateFilterLinks', value)
  })

  watch(
    selectedCultures,
    (value) => {
      browserStorage.set('productTable-selectedCultures', value)
    },
    { deep: true },
  )

  // show / clear imported products msg
  watch(latestImportedProducts, (value) => {
    if (value?.imported?.length) {
      showRecentImportedProductsMsg.value = true
    }
  })

  watch(
    () => route.fullPath,
    () => {
      showRecentImportedProductsMsg.value = false
    },
  )

  watch(
    () => propertyStore.storedProperties,
    (_newValue, prevValue) => {
      if (prevValue?.length) {
        updateProperties()
      }
    },
  )

  watch(
    () => taxonomiesStore.taxonomies.attributes,
    async () => {
      updateProperties()
    },
  )

  watch(
    () => editMode.value,
    (editable) => {
      if (editable) {
        taxonomiesStore.clearTaxonomicCache()
      }
    },
  )

  // ---------------------
  // -- Lifecycle hooks --
  // ---------------------
  onMounted(() => {
    updateProperties()
    selectedCultures.value = browserStorage.get('productTable-selectedCultures') || (store.dataCulture ? [store.dataCulture] : [])
  })

  // -------------------------
  // -- Functions (actions) --
  // -------------------------

  const updateProperties = () => {
    initProperties(true)
  }

  const restoreMissingProducts = async () => {
    // add products that no longer matches with query but have changes in the session
    // can happen in cases after loading a session if changes has been made on a product and the query saved with the session no longer matches
    const response = await DataService.getProductsSkus(route.query)
    const skusMatchingQuery = new Set(response.data)

    const productInputsNotMatchingFilter = productInputEntries.value.reduce((acc: string[], [id]) => {
      if (!skusMatchingQuery.has(id)) {
        acc.push(id)
      }
      return acc
    }, [])

    // choose to include or remove changes for products no longer matching current filter
    if (productInputsNotMatchingFilter.length) {
      const products = await DataService.getProductsBySku(productInputsNotMatchingFilter)
      extraProducts.value = products.data
    } else {
      extraProducts.value = []
    }

    // restore the rest of the missing products as new rows
    const extras = new Set(extraProducts.value.map((p) => p.sku))

    newProducts.value = productInputsNotMatchingFilter.reduce((acc: SimplifiedProduct[], sku) => {
      if (!extras.has(sku)) {
        const newProduct = {
          sku,
          hasTemporarySku: true,
        } as SimplifiedProduct
        acc.push(newProduct)
      }
      return acc
    }, [])
  }

  const toggleAllCultures = () => {
    if (allCulturesSelected.value) {
      selectedCultures.value.length = 0
    } else {
      selectedCultures.value.length = 0
      store.availableCultures.forEach((culture: Culture) => {
        selectedCultures.value?.push(culture)
      })
    }
  }

  const toggleCulture = (culture: Culture) => {
    if (cultureIsIncluded(selectedCultures.value, culture)) {
      selectedCultures.value = selectedCultures.value.filter(
        (selectedCulture) => !(selectedCulture.country === culture.country && selectedCulture.language === culture.language),
      )
    } else {
      selectedCultures.value.push(culture)
    }
  }

  const isModified = (product: SimplifiedProduct, property?: EditableAdvancedProperty) => {
    if (property) {
      const key = property.defaultHeader
      return modified.value?.[product.sku]?.[key] !== undefined
    } else {
      return !!Object.keys(modified.value?.[product.sku] || {}).length
    }
  }

  const updateAllModified = (
    skus?: string[] | Set<string>,
    originalProducts: Partial<Record<string, SimplifiedProduct>> = sessionProductsBySku.value,
    productInput = stateManager.currentState.value,
    resetChangesThatAreNoLongerModified = true,
  ) => {
    skus?.forEach((originalSku) => updateModified(originalSku, originalProducts[originalSku], productInput[originalSku], resetChangesThatAreNoLongerModified))
  }

  /**
   * Updates modified state
   * Also resets property if no longer modified
   * @param sku
   * @param resetChangesThatAreNoLongerModified checks the value for each property if they are still modified and clears the property + modified state if they aren't (mainly used for debugging)
   */
  const updateModified = (
    sku: string,
    product = sessionProductsBySku.value[sku],
    productInput = stateManager.currentState.value[sku],
    resetChangesThatAreNoLongerModified = true,
  ) => {
    // restore modified state
    modified.value[sku] = {}

    previewProperties.value?.map((property) => {
      const result = property.get(product, productInput)

      if (result?.modified?.status || result?.modified?.value) {
        setModified(sku, property, true)
      } else if (resetChangesThatAreNoLongerModified) {
        if (product && isModified(product, property)) {
          property.reset(productInput, product)
          console.log(`reset property ${property.defaultHeader} on ${sku} since it's not modified anymore`)

          if (editMode.value) {
            setModified(sku, property, false)
          }
        }
      }
    })
  }

  const setModified = (sku: string, property: EditableAdvancedProperty, containsChanges?: boolean) => {
    const key = property?.defaultHeader

    if (containsChanges) {
      if (!modified.value[sku]) {
        modified.value[sku] = {}
      }
      modified.value[sku][key] = containsChanges
    } else {
      delete modified.value[sku]?.[key]
    }
  }

  const setModifiedMultiple = (sku: string, propertyStateObjects: { property: EditableAdvancedProperty; containsChanges?: boolean }[]) => {
    for (const item of propertyStateObjects) {
      setModified(sku, item.property, item.containsChanges)
    }
  }

  /**
   * Remove all empty input objects in current state
   * Should be followed by an ensureFullProductInputs
   */
  const trimCurrentState = () => {
    Object.entries(stateManager.currentState.value).forEach(([id, rowState]) => {
      if (Object.keys(rowState).length === 0 /*&& !sessionProductsBySku.value[id]*/) {
        delete stateManager.currentState.value[id]
      }
    })
  }

  /** Ensures theres an input object for every session product */
  const ensureFullProductInputs = () => {
    sessionProducts.value?.forEach((product) => {
      if (product && !stateManager.currentState.value[product.sku]) {
        stateManager.currentState.value[product.sku] = {}
      }
    })

    return stateManager.currentState
  }

  // -------------------------------
  // -- History related functions --
  // -------------------------------

  // -- Private --
  const historyCb = () => {
    const editedSkus = productInputEntries.value.reduce((acc, [id]) => {
      acc.add(id)
      return acc
    }, new Set<string>())

    updateAllModified(editedSkus)
    sessionStore.saveCurrentSession()

    // delete removed rows from newProducts
    newProducts.value = newProducts.value.filter((product) => editedSkus.has(product.sku))

    const sessionProductsSkus = new Set(
      sessionProducts.value.reduce((acc: string[], product) => {
        if (product) {
          acc.push(product.sku)
        }
        return acc
      }, []),
    )

    // restore deleted products
    editedSkus.forEach((sku) => {
      if (!sessionProductsSkus.has(sku)) {
        newProducts.value.push({ sku, hasTemporarySku: !stateManager.currentState.value[sku]?.sku } as SimplifiedProduct)
      }
    })
  }

  // -- Public --
  const resetProduct = (product: SimplifiedProduct) => {
    // if (confirm(this.appTranslate('Do you want to reset all the product changes?'))) {
    delete modified.value[product.sku]
    stateManager.currentState.value[product.sku] = { sku: product.hasTemporarySku ? '' : product.sku }
    stateManager.pushHistoryEntry([product.sku])
    propertyStore.incPropertyResetKey(product.sku)
    // }
  }

  const removeNewProduct = (sku: string) => {
    // if (confirm(this.appTranslate('Do you want to remove the product row?'))) {
    newProducts.value = newProducts.value.filter((product) => product.sku !== sku)
    delete modified.value[sku]
    delete stateManager.currentState.value[sku]
    stateManager.pushHistoryEntry([sku])
    propertyStore.incPropertyResetKey(sku)
    // }
  }

  const pushHistoryEntry = (ids?: Array<{ product: SimplifiedProduct; property?: AdvancedProperty }>, queueSessionSave = true) => {
    stateManager.pushHistoryEntry(ids?.map(({ product, property }) => product.sku + '_' + property?.defaultHeader))

    if (queueSessionSave) {
      sessionStore.saveCurrentSession()
    }
  }

  return {
    // reactives
    extraProducts,
    newProducts,
    editMode,
    modified,
    activateProductLinks,
    latestImportedProducts,
    properties,
    selectedPropertyKeys,
    selectedCultures,
    selectedDeadlines,

    // computed
    newProductsSkus,
    newProductsBySku,
    sessionProducts,
    sessionProductsBySku,
    allProducts,
    recentImportedProductsNotAmongResult,
    productInputEntries,
    productInputBySku,
    skeletonRows,
    totalRows,

    nonDefaultCulturalProperties,
    nonDefaultCulturalPropertiesByComparable,
    stickyProperties,
    selectedProperties,
    combinedProperties,
    combinedPropertiesByComparable,
    previewProperties,
    propertyKeysIncludedInSelectedDeadlines,
    allCulturesSelected,
    isPristine,
    showRecentImportedProductsMsg,

    // functions
    restoreMissingProducts,
    isModified,
    toggleAllCultures,
    toggleCulture,
    updateAllModified,
    updateModified,
    setModified,
    setModifiedMultiple,
    updateProperties,
    trimCurrentState,
    ensureFullProductInputs,

    // history state composable functions and computed props
    currentState: stateManager.currentState,
    pushHistoryEntry,
    resetProduct,
    removeNewProduct,
    undo: () => stateManager.undo(historyCb),
    redo: () => stateManager.redo(historyCb),
    undoStates: stateManager.undoStates,
    redoStates: stateManager.redoStates,
    historyUpdateKey: stateManager.latestHistoryChange,
    initiateState: stateManager.initiateState,
    clearState: stateManager.clearState,
    debugHistory: stateManager.debugHistory,
  }
})
